import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { combineLatest, EMPTY, exhaustMap, from, mergeMap, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import * as profileActions from 'app/actions/profile';
import * as fromProfile from 'app/reducers/profile';
import { IdentityService } from 'app/services/identity.service';
import { LanguageService } from 'app/services/language.service';
import { LicenseService } from 'app/services/license.service';
import { UsersService } from 'app/services/users.service';
import { UserSettings } from 'app/models/api/user-settings';
import { assetsActions } from 'app/actions/assets';
import * as sapAssetsActions from 'app/actions/sap-assets';
import { tableFiltersAndMetaActions, tableWidthActions } from 'app/actions/table-store-data';
import { ProfileService } from 'app/services/profile.service';
import { AccountSettings, defaultAccountSettings } from 'app/models/api/account-settings';
import { Identity } from 'app/models/api/identity';
import { Router } from '@angular/router';
import { concatLatestFrom } from '@ngrx/operators';
import * as fromAssets from 'app/reducers/assets';
import { ThemeService } from '../services/theme/theme.service';
import { StoreHelperService } from '../services/store-helper/store-helper.service';
import { FeatureFlagService } from 'app/services/feature-flag.service';
import { LicenseFeatureFlag } from 'app/models/license';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'environments/environment';
import { VersionIntroService } from 'app/services/version-intro.service';
import { ApplicationStore } from 'app/state-management/application/application.store';
import { LogoutService } from '../standalone-core/features';
import { AppBootstrapErrorService } from 'app/services/app-bootstrap-error.service';
import { NavigationService } from 'app/services/navigation.service';
import { SmartenanceError } from 'app/models/errors';

@Injectable()
export class ProfileEffects {
  private readonly actions$: Actions = inject(Actions);
  private readonly store: Store = inject(Store);
  private readonly identityService: IdentityService = inject(IdentityService);
  private readonly router: Router = inject(Router);
  private readonly language: LanguageService = inject(LanguageService);
  private readonly usersService: UsersService = inject(UsersService);
  private readonly licenseService: LicenseService = inject(LicenseService);
  private readonly logoutService: LogoutService = inject(LogoutService);
  private readonly profileService: ProfileService = inject(ProfileService);
  private readonly themeService: ThemeService = inject(ThemeService);
  private readonly storeHelperService: StoreHelperService = inject(StoreHelperService);
  private readonly featureFlagService: FeatureFlagService = inject(FeatureFlagService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly versionIntroService: VersionIntroService = inject(VersionIntroService);
  private readonly appStore: ApplicationStore = inject(ApplicationStore);
  private readonly appBootstrapErrorService: AppBootstrapErrorService = inject(AppBootstrapErrorService);
  private readonly navigationService: NavigationService = inject(NavigationService);

  initLayoutPage$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(profileActions.initLayoutPage),
      mergeMap(() => [
        profileActions.loadUserSettings(),
        profileActions.loadIdentity(),
        tableWidthActions.getDataFromLocalStorage(),
        tableFiltersAndMetaActions.getSapFiltersFromStorage()
      ])
    )
  );

  initDataOnLicenseAvailable$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(profileActions.loadIdentitySuccess),
      concatLatestFrom(() => this.store.select(fromAssets.selectReloadInitial)),
      mergeMap(([{ license }, assetTreeAlreadyInitialized]) => {
        if (license?.hasNoActiveLicense) {
          return [];
        }
        return assetTreeAlreadyInitialized
          ? []
          : [assetsActions.initAssetTree()];
      })
    )
  );

  loadProfileAndLicense$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(profileActions.loadIdentity),
      exhaustMap(() =>
        combineLatest([this.identityService.getIdentity(), this.licenseService.getLicenseInfo()]).pipe(
          mergeMap(([identity, license]) =>
            license.hasSapConnectorLicense && identity.sapConnectorAgreementAccepted
              ? [
                profileActions.loadIdentitySuccess({ identity, license }),
                profileActions.loadSapData(),
                sapAssetsActions.initSapAssetTree(),
                profileActions.checkVersionIntro()
              ]
              : [
                profileActions.loadIdentitySuccess({ identity, license }),
                profileActions.checkVersionIntro()
              ]
          ),
          catchError(error => of(profileActions.loadIdentityFailed({ error })))
        )
      )
    )
  );

  navigateToServiceUnavailableOnLoadIdentityFailed$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(profileActions.loadIdentityFailed),
        map(({ error }) => error as SmartenanceError),
        tap((error) => {
          this.appBootstrapErrorService.setError(error);
          this.navigationService.navigateToServiceUnavailable();
        }),
      ),
    { dispatch: false }
  );

  updateProfile$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(profileActions.updateIdentity),
      switchMap(action =>
        this.identityService.updateProfileName(action.changedName).pipe(
          map(identity => profileActions.updateIdentitySuccess({ identity })),
          catchError(error => of(profileActions.updateIdentityFail({ error })))
        )
      )
    )
  );

  reloadProfile$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(profileActions.reloadIdentity),
      switchMap(() =>
        this.appStore.reloadIdentity().pipe(
          map(identity => profileActions.updateIdentitySuccess({ identity })),
          catchError(error => of(profileActions.updateIdentityFail({ error })))
        )
      )
    )
  );

  updateIdentitySuccess$: Observable<unknown> = createEffect(() => this.actions$.pipe(
    ofType(profileActions.updateIdentitySuccess),
    tap(() => this.appStore.showSnack({
      disappearAfter: 0,
      text: this.translateService.instant('PROFILE.PROFILE_INFO_AFTER_CHANGES'),
      type: 'warning',
      showClose: true
    }))
  ), { dispatch: false });

  deleteProfile$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(profileActions.deleteIdentity),
      concatLatestFrom(() => this.store.select(fromProfile.selectProfile)),
      switchMap(([, currentProfile]) =>
        this.usersService.deleteUsers([currentProfile.userId]).pipe(
          map(() => profileActions.deleteIdentitySuccess()),
          tap(() => this.logoutService.logout()),
          catchError(error => of(profileActions.deleteIdentityFail({ error })))
        )
      )
    )
  );

  loadUserSettings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(profileActions.loadUserSettings),
      switchMap(() =>
        this.language.getUserSettings().pipe(
          tap((userSettings: UserSettings) => {
            if (userSettings.langCode) {
              this.language.activateLanguage(userSettings.langCode);
            }
          }),
          map((userSettings: UserSettings) => ({ ...userSettings })),
          map(userSettings => profileActions.loadUserSettingsSuccess({ userSettings })),
          catchError(error => of(profileActions.loadUserSettingsFail({ error })))
        )
      )
    )
  );

  updateUserSettings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(profileActions.updateUserSettings),
      map(action => action.userSettings),
      concatLatestFrom(() => this.store.select(fromProfile.selectUserSettings)),
      map(([userSettingsUpdate, previousUserSettings]) => ({ ...previousUserSettings, ...userSettingsUpdate })),
      switchMap(userSettings =>
        this.language.updateUserSettings(userSettings).pipe(
          map(() => profileActions.updateUserSettingsSuccess({ userSettings })),
          catchError(error => of(profileActions.updateUserSettingsFail({ error })))
        )
      )
    )
  );

  initAccountSetting$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(profileActions.initAccountSettings),
      map(() => this.profileService.getAccountSetting() ?? { ...defaultAccountSettings }),
      map((accountSettings: AccountSettings) => profileActions.updateAccountSettings({ accountSettings }))
    )
  );

  updateUpcomingTasksDialogAvailabilityOnCompleteSetting$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(profileActions.updateUpcomingTasksDialogAvailabilityOnCompleteSetting),
        tap(action => this.profileService.setUpcomingTasksDialogAvailabilityOnCompleteSetting(action.state))
      ),
    { dispatch: false }
  );

  loadSapData$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(profileActions.loadSapData),
      concatLatestFrom(() => [
        this.store.select(fromProfile.selectProfile),
        this.store.select(fromProfile.getSapLanguage)
      ]),
      switchMap(([, identity, sapLanguage]: [Action, Identity, string]) =>
        this.profileService.loadSapData(identity, sapLanguage).pipe(
          map(sapData => profileActions.loadSapDataSuccess({ ...sapData })),
          catchError(error => of(profileActions.loadSapDataFailSnack({
            error,
            snack: {
              messageTranslationKey: 'MISC.SAP_CONNECTION',
              actionButtonTranslationKey: 'COMMON.NAVIGATE',
              actionForOk: () => this.store.dispatch(profileActions.navigateToAdministrationSap())
            }
          })))
        )
      )
    )
  );

  refreshTheme$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(profileActions.reloadTheme),
      switchMap((action) =>
        from(this.themeService.reloadTheme(action.identity)).pipe(
          map(() => {
            this.storeHelperService.showSuccessSnack('PROFILE.RELOAD_THEME_SUCCESS');
            return profileActions.reloadThemeSuccess();
          }),
          catchError(error => of(profileActions.reloadThemeFailed(error))),
        )
      )
    )
  );

  reloadThemeFailed$ = createEffect(() => this.actions$.pipe(
    ofType(profileActions.reloadThemeFailed),
    tap(() => {
      this.storeHelperService.showErrorSnack('PROFILE.RELOAD_THEME_FAILURE');
      this.themeService.setDefaults();
    }),
  ), { dispatch: false });

  navigateToAdministrationSap$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(profileActions.navigateToAdministrationSap),
    tap(() => this.router.navigate(['/administration', 'sap-synchronization']))
  ), { dispatch: false });

  // The cookie "ecn-version-intro" saves the last shown version.
  // If the cookie doesn't exist or is an old version the version intro will be shown.
  // On new computers / browsers the version intro will not be shown.
  //
  // The flag is read once and the snackbar cannot be taken back, so the read waits for the
  // theme. Without that wait a theme carrying hideVersionIntro loses the race against
  // /me/info on the login page, and the snackbar is shown even though the theme disables it.
  checkVersionIntro$ = createEffect(() =>
    this.actions$.pipe(
      ofType(profileActions.checkVersionIntro),
      switchMap(() =>
        this.themeService.themeSettled$.pipe(
          filter((settled: boolean) => settled),
          take(1),
          switchMap(() => from(this.featureFlagService.featureOnLocal(LicenseFeatureFlag.VERSION_INTRO))),
          mergeMap(showVersionIntro => {
            if (!showVersionIntro) {
              return of(profileActions.closeVersionIntro());
            }
            const currentVersion = environment.version;
            if (currentVersion.split('.').length < 3) {
              return EMPTY;
            }
            if (this.versionIntroService.shouldShowVersionIntro(currentVersion)) {
              return of(profileActions.showVersionIntroSnackbar());
            }
            return EMPTY;
          })
        )
      )
    )
  );

  showVersionIntroSnackbar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(profileActions.showVersionIntroSnackbar),
      tap(() => this.appStore.showSnack({
        disappearAfter: 0,
        text: this.translateService.instant('VERSION_INTRO.SNACKBAR_LABEL'),
        variant: 'c',
        type: 'info',
        showClose: true,
        action: this.translateService.instant('VERSION_INTRO.ACTION_BUTTON_LABEL'),
        closeOnAction: true,
        actionForOk: () => this.store.dispatch(profileActions.navigateToReleaseNotes()),
        actionForClose: () => this.store.dispatch(profileActions.closeVersionIntro())
      }))
    ),
    { dispatch: false }
  );

  navigateToReleaseNotes$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(profileActions.navigateToReleaseNotes),
        tap(() => {
          this.store.dispatch(profileActions.closeVersionIntro());
          this.router.navigate(['administration', 'release-notes'], { queryParams: { expandLatestRelease: true } });
        })
      ),
    { dispatch: false }
  );

  closeVersionIntro$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(profileActions.closeVersionIntro),
        tap(() => {
          this.versionIntroService.setVersionIntroCookie();
        })
      ),
    { dispatch: false }
  );

}
